import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, PLATFORM_ID, effect, inject, input, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { GsFocusTrapDirective } from '../../directives/focus-trap.directive';

@Component({
  selector: 'gs-alert',
  imports: [CommonModule, GsFocusTrapDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './gs-alert.component.html'
})
export class GsAlertComponent {
  // Services
  /** Whether the component runs in the browser; the server render never moves the element to the body (hydration) */
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // Inputs & Models
  /** Label shown in the header */
  title = input<string>();
  /** Message text inside the alert */
  message = input<string>();
  /** Allow closing by clicking the backdrop */
  enableBackdrop = input(true);
  /** Block dismissal until a decision is made */
  forceInput = input(false);
  /** Whether the header is rendered */
  showHeader = input(true);
  /** Round the corners */
  roundedCorners = input(true);
  /** Custom classes for the container */
  alertStyle = input<string>();
  /** Custom classes for the header */
  headerStyle = input<string>();
  /** Custom classes for the body */
  bodyStyle = input('text-d-medium-main border-b border-gray-200 bg-white');
  /** Custom classes for the footer */
  footerStyle = input<string>();
  /** Custom classes for the text color */
  textStyle = input<string>();
  /** Accessible dialog name, used as `aria-label` when no header is shown */
  ariaLabel = input<string>();

  // Properties & Signals
  /** Incrementing source for unique, per-instance element ids */
  private static nextId = 0;
  /** Unique id of the title element, referenced via `aria-labelledby` */
  readonly titleId = `gs-alert-title-${GsAlertComponent.nextId++}`;
  /** Unique id of the message element, referenced via `aria-describedby` */
  readonly messageId = `gs-alert-message-${GsAlertComponent.nextId++}`;
  /** Open state */
  readonly opened = signal(false);

  // ViewChilds
  private readonly alertRef = viewChild<ElementRef<HTMLDivElement>>('alertRef');

  constructor() {
    effect(() => {
      const element = this.alertRef()?.nativeElement;
      if (!element) return;
      const isOpen = this.opened();
      element.style.display = isOpen ? 'flex' : 'none';
      if (isOpen && this.isBrowser && element.parentElement !== document.body) {
        document.body.appendChild(element);
      }
    });
  }

  // Methods
  /** Toggles the open state */
  toggle() {
    this.opened.set(!this.opened());
  }

  /** Dismisses the alert unless `forceInput` is set */
  dismiss() {
    if (this.forceInput()) return;
    this.opened.set(false);
  }
}
